import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  DatePicker,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Textarea,
  Tooltip,
  useDisclosure
} from '@nextui-org/react';
import { parseAbsoluteToLocal } from '@internationalized/date';
import { apiFetch, apiTry } from '../lib/api';
import { IMPORT_ACCEPT, parsePastedList, readIdentifiersFromFile } from '../lib/importList';
import { useAuth } from '../hooks/useAuth';

// Reactivación de la oferta Founders por lotes. Cada bulk reabre la compra de
// Founders (mismo precio de siempre) a un conjunto de usuarios durante una
// ventana propia, aunque la ventana global lleve meses cerrada.

const DURATIONS = [
  { key: '24h', label: '24 horas' },
  { key: '48h', label: '48 horas' },
  { key: '72h', label: '72 horas' },
  { key: '1w', label: '1 semana' },
  { key: 'forever', label: 'Para siempre' }
];

// Plazos que se pueden SUMAR sobre el fin actual (no incluye "para siempre",
// que no es una ampliación sino quitar el límite).
const EXTENSIONS = DURATIONS.filter((d) => d.key !== 'forever');

const DURATION_MS = {
  '24h': 24 * 3600e3,
  '48h': 48 * 3600e3,
  '72h': 72 * 3600e3,
  '1w': 7 * 24 * 3600e3,
  forever: null
};

const DURATION_LABEL = {
  ...Object.fromEntries(DURATIONS.map((d) => [d.key, d.label])),
  // Resultado de ampliar el plazo o fijar una fecha: ya no deriva de una duración.
  custom: 'Fecha fija'
};

// ISO/ms → ZonedDateTime local para el DatePicker.
function toZoned(ms) {
  if (!ms || !Number.isFinite(Number(ms))) return null;
  return parseAbsoluteToLocal(new Date(Number(ms)).toISOString());
}

function fmt(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function remainingLabel(endsAt) {
  if (endsAt === null || endsAt === undefined) return 'Para siempre';
  const diff = endsAt - Date.now();
  if (diff <= 0) return 'Caducado';
  const hours = Math.floor(diff / 3600000);
  if (hours < 48) return `${hours} h restantes`;
  return `${Math.floor(hours / 24)} d restantes`;
}

function bulkChip(bulk) {
  if (bulk.status === 'archived') return <Chip size="sm" variant="flat">Archivado</Chip>;
  if (bulk.forever) return <Chip size="sm" color="success" variant="flat">Activo · para siempre</Chip>;
  if (bulk.endsAt <= Date.now()) return <Chip size="sm" color="default" variant="flat">Caducado</Chip>;
  return <Chip size="sm" color="success" variant="flat">Activo · {remainingLabel(bulk.endsAt)}</Chip>;
}

// Nombre por defecto: "Bulk de N Founders · 17 ago 2026".
function defaultBulkName(count) {
  const date = new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  return `Bulk de ${count} Founders · ${date}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Panel de importación + validación (compartido: crear bulk / añadir usuarios)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Carga la lista (archivo o pegada), la valida contra el worker y deja que el
 * admin decida qué hacer con los dos avisos que pidió producto:
 *   · usuario que ya está en otro bulk activo
 *   · usuario que nunca fue Founder
 * La decisión es global por categoría, con override por fila en la tabla.
 */
function ImportPanel({ excludeBulkId, onSelectionChange }) {
  const fileRef = useRef(null);
  const [pasted, setPasted] = useState('');
  const [fileName, setFileName] = useState('');
  const [entries, setEntries] = useState([]);
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // 'skip' = no añadirlos · 'move' = quitarlos del bulk antiguo
  const [conflictPolicy, setConflictPolicy] = useState('skip');
  // 'skip' = crear el bulk sin ellos · 'include' = añadirlos igualmente
  const [nonFounderPolicy, setNonFounderPolicy] = useState('skip');
  const [overrides, setOverrides] = useState({}); // uid → bool (incluir)

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const list = await readIdentifiersFromFile(file);
      if (!list.length) {
        setError('No se ha encontrado ninguna columna con uid, email o @usuario en ese archivo.');
        return;
      }
      setFileName(`${file.name} · ${list.length} entradas`);
      setEntries(list);
      setRows(null);
      setSummary(null);
    } catch (err) {
      setError(`No se pudo leer el archivo: ${err.message}`);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function handlePaste(value) {
    setPasted(value);
    setFileName('');
    setEntries(parsePastedList(value));
    setRows(null);
    setSummary(null);
  }

  async function validate() {
    if (!entries.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/backoffice/founders/reactivations/resolve', {
        method: 'POST',
        body: { entries, excludeBulkId: excludeBulkId || undefined }
      });
      setRows(res.rows || []);
      setSummary(res.summary || null);
      setOverrides({});
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // Fila incluible: existe, no es duplicado y no es Founder con licencia viva
  // (a ese no hay nada que reactivarle).
  const selectable = useMemo(
    () => (rows || []).filter((r) => r.found && !r.duplicate && !r.activeFounder),
    [rows]
  );

  function defaultIncluded(row) {
    if (row.conflict && conflictPolicy === 'skip') return false;
    if (!row.wasFounder && nonFounderPolicy === 'skip') return false;
    return true;
  }

  function isIncluded(row) {
    return overrides[row.uid] !== undefined ? overrides[row.uid] : defaultIncluded(row);
  }

  const selection = useMemo(() => {
    const included = selectable.filter(isIncluded);
    return {
      members: included.map((r) => ({ uid: r.uid, email: r.email, sourceRef: r.input })),
      moveUids: included.filter((r) => r.conflict).map((r) => r.uid),
      count: included.length
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectable, overrides, conflictPolicy, nonFounderPolicy]);

  useEffect(() => {
    onSelectionChange?.(selection);
  }, [selection, onSelectionChange]);

  const conflicts = selectable.filter((r) => r.conflict);
  const nonFounders = selectable.filter((r) => !r.wasFounder);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-default-400">
            Archivo CSV o Excel
          </span>
          <input
            ref={fileRef}
            type="file"
            accept={IMPORT_ACCEPT}
            onChange={handleFile}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-default-100 file:px-3 file:py-2 file:text-sm"
          />
          <p className="text-xs text-default-400">
            Se coge automáticamente la columna con uid, email o @usuario. El resto se ignora.
          </p>
          {fileName && <span className="text-xs text-success-600">{fileName}</span>}
        </div>

        <Textarea
          label="…o pega la lista a mano"
          placeholder={'uid, email o @usuario\nuno por línea'}
          minRows={4}
          value={pasted}
          onValueChange={handlePaste}
        />
      </div>

      <div className="flex items-center gap-3">
        <Button
          color="primary"
          variant="flat"
          onPress={validate}
          isLoading={busy}
          isDisabled={!entries.length}
        >
          Validar {entries.length ? `(${entries.length})` : ''}
        </Button>
        {rows && (
          <span className="text-sm text-default-500">
            {selection.count} de {rows.length} se añadirán
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>
      )}

      {summary && (
        <div className="flex flex-wrap gap-2">
          <Chip size="sm" color="success" variant="flat">{summary.ok} listos</Chip>
          {summary.notFound > 0 && (
            <Chip size="sm" color="danger" variant="flat">{summary.notFound} no encontrados</Chip>
          )}
          {summary.notFounder > 0 && (
            <Chip size="sm" color="warning" variant="flat">{summary.notFounder} nunca fueron Founder</Chip>
          )}
          {summary.conflicts > 0 && (
            <Chip size="sm" color="warning" variant="flat">{summary.conflicts} ya en otro bulk</Chip>
          )}
          {summary.activeFounder > 0 && (
            <Chip size="sm" variant="flat">{summary.activeFounder} ya son Founder activo</Chip>
          )}
          {summary.duplicates > 0 && (
            <Chip size="sm" variant="flat">{summary.duplicates} duplicados</Chip>
          )}
          {summary.errors > 0 && (
            <Chip size="sm" color="danger" variant="flat">{summary.errors} con error</Chip>
          )}
        </div>
      )}

      {conflicts.length > 0 && (
        <Card className="border border-warning-200 bg-warning-50/50">
          <CardBody className="gap-2">
            <span className="text-sm font-semibold text-warning-700">
              {conflicts.length} usuario(s) ya están en un bulk activo
            </span>
            <RadioGroup
              size="sm"
              orientation="horizontal"
              value={conflictPolicy}
              onValueChange={(v) => { setConflictPolicy(v); setOverrides({}); }}
            >
              <Radio value="skip">No añadirlos a este bulk</Radio>
              <Radio value="move">Quitarlos del bulk antiguo y ponerlos en este</Radio>
            </RadioGroup>
          </CardBody>
        </Card>
      )}

      {nonFounders.length > 0 && (
        <Card className="border border-warning-200 bg-warning-50/50">
          <CardBody className="gap-2">
            <span className="text-sm font-semibold text-warning-700">
              {nonFounders.length} usuario(s) nunca han sido Founder
            </span>
            <RadioGroup
              size="sm"
              orientation="horizontal"
              value={nonFounderPolicy}
              onValueChange={(v) => { setNonFounderPolicy(v); setOverrides({}); }}
            >
              <Radio value="skip">Crear el bulk sin ellos</Radio>
              <Radio value="include">Añadirlos igualmente</Radio>
            </RadioGroup>
          </CardBody>
        </Card>
      )}

      {rows && (
        <div className="max-h-[340px] overflow-auto rounded-md border border-default-200">
          <Table removeWrapper aria-label="Resultado de la validación" isCompact>
            <TableHeader>
              <TableColumn>Incluir</TableColumn>
              <TableColumn>Entrada</TableColumn>
              <TableColumn>Usuario</TableColumn>
              <TableColumn>Founder</TableColumn>
              <TableColumn>Aviso</TableColumn>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const blocked = !r.found || r.duplicate || r.activeFounder;
                return (
                  <TableRow key={`${r.input}-${i}`}>
                    <TableCell>
                      <Checkbox
                        size="sm"
                        isDisabled={blocked}
                        isSelected={!blocked && isIncluded(r)}
                        onValueChange={(v) => setOverrides((o) => ({ ...o, [r.uid]: v }))}
                      />
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate font-mono text-xs">{r.input}</TableCell>
                    <TableCell className="text-xs">
                      {r.found ? (
                        <div className="flex flex-col">
                          <span>{r.email || '—'}</span>
                          <span className="font-mono text-default-400">{r.uid}</span>
                        </div>
                      ) : (
                        <span className="text-default-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.founderNumber != null
                        ? `#${String(r.founderNumber).padStart(4, '0')}`
                        : r.found ? '—' : ''}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.error && <Chip size="sm" color="danger" variant="flat">Error: {r.error}</Chip>}
                      {!r.found && !r.error && <Chip size="sm" color="danger" variant="flat">No encontrado</Chip>}
                      {r.duplicate && <Chip size="sm" variant="flat">Duplicado</Chip>}
                      {r.activeFounder && <Chip size="sm" variant="flat">Ya es Founder activo</Chip>}
                      {r.conflict && (
                        <Chip size="sm" color="warning" variant="flat">
                          En «{r.conflict.bulkName}»
                        </Chip>
                      )}
                      {r.found && !r.wasFounder && !r.duplicate && (
                        <Chip size="sm" color="warning" variant="flat">Nunca fue Founder</Chip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Crear bulk
// ═══════════════════════════════════════════════════════════════════════════

function CreateBulkModal({ isOpen, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [durationKey, setDurationKey] = useState('72h');
  const [selection, setSelection] = useState({ members: [], moveUids: [], count: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDurationKey('72h');
      setSelection({ members: [], moveUids: [], count: 0 });
      setError(null);
    }
  }, [isOpen]);

  const onSelectionChange = useCallback((s) => setSelection(s), []);

  async function create() {
    setSaving(true);
    setError(null);
    const res = await apiTry('/api/backoffice/founders/reactivations', {
      method: 'POST',
      body: {
        name: name.trim() || defaultBulkName(selection.count),
        durationKey,
        members: selection.members,
        moveUids: selection.moveUids
      }
    });
    setSaving(false);

    if (!res.ok) {
      const d = res.data;
      if (d?.conflicts?.length) {
        setError(
          `Conflicto: ${d.conflicts.length} usuario(s) siguen en otro bulk activo. ` +
          'Elige "Quitarlos del bulk antiguo" o desmárcalos y vuelve a intentarlo.'
        );
      } else {
        setError(d?.error || `Error ${res.status}`);
      }
      return;
    }

    onCreated?.(res.data);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>Nuevo bulk de reactivación</span>
          <span className="text-xs font-normal text-default-500">
            Vuelve a mostrar la pestaña Founders en Gestionar Suscripción a estos usuarios,
            al mismo precio de siempre, durante la ventana que elijas.
          </span>
        </ModalHeader>
        <ModalBody className="gap-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              label="Nombre del bulk"
              placeholder={defaultBulkName(selection.count || 120)}
              value={name}
              onValueChange={setName}
              description="Si lo dejas vacío se genera con el número de usuarios y la fecha."
            />
            <Select
              label="Duración de la reactivación"
              selectedKeys={[durationKey]}
              onSelectionChange={(keys) => setDurationKey([...keys][0])}
              description="Cuenta desde el momento de crear el bulk."
            >
              {DURATIONS.map((d) => (
                <SelectItem key={d.key}>{d.label}</SelectItem>
              ))}
            </Select>
          </div>

          <ImportPanel onSelectionChange={onSelectionChange} />

          {error && (
            <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancelar bulk</Button>
          <Button
            color="primary"
            onPress={create}
            isLoading={saving}
            isDisabled={selection.count === 0}
          >
            Crear bulk con {selection.count} usuario(s)
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Añadir usuarios a un bulk existente
// ═══════════════════════════════════════════════════════════════════════════

function AddMembersModal({ isOpen, onClose, bulk, onDone }) {
  const [selection, setSelection] = useState({ members: [], moveUids: [], count: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelection({ members: [], moveUids: [], count: 0 });
      setError(null);
    }
  }, [isOpen]);

  const onSelectionChange = useCallback((s) => setSelection(s), []);

  async function add() {
    setSaving(true);
    setError(null);
    const res = await apiTry(`/api/backoffice/founders/reactivations/${bulk.id}/members`, {
      method: 'POST',
      body: { members: selection.members, moveUids: selection.moveUids }
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.data?.error || `Error ${res.status}`);
      return;
    }
    onDone?.();
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>Añadir usuarios a «{bulk?.name}»</span>
          <span className="text-xs font-normal text-default-500">
            Su ventana arranca ahora con la duración del bulk ({DURATION_LABEL[bulk?.durationKey]}),
            no desde la fecha de creación.
          </span>
        </ModalHeader>
        <ModalBody>
          <ImportPanel excludeBulkId={bulk?.id} onSelectionChange={onSelectionChange} />
          {error && (
            <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancelar</Button>
          <Button color="primary" onPress={add} isLoading={saving} isDisabled={!selection.count}>
            Añadir {selection.count} usuario(s)
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Detalle de un bulk
// ═══════════════════════════════════════════════════════════════════════════

function BulkDetailModal({ bulkId, isOpen, onClose, onChanged, canWrite }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [durationKey, setDurationKey] = useState('72h');
  const [endValue, setEndValue] = useState(null);
  const addMembers = useDisclosure();

  const reload = useCallback(async () => {
    if (!bulkId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/backoffice/founders/reactivations/${bulkId}`);
      setData(res);
      setName(res.bulk.name);
      setDurationKey(res.bulk.durationKey);
      setEndValue(toZoned(res.bulk.endsAt));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [bulkId]);

  useEffect(() => {
    if (isOpen) reload();
  }, [isOpen, reload]);

  async function patchBulk(body) {
    setBusy(true);
    setError(null);
    const res = await apiTry(`/api/backoffice/founders/reactivations/${bulkId}`, {
      method: 'PATCH',
      body
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.data?.error || `Error ${res.status}`);
      return;
    }
    await reload();
    onChanged?.();
  }

  async function patchMember(uid, body) {
    setBusy(true);
    setError(null);
    const res = await apiTry(`/api/backoffice/founders/reactivations/${bulkId}/members/${uid}`, {
      method: 'PATCH',
      body
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.data?.error || `Error ${res.status}`);
      return;
    }
    await reload();
    onChanged?.();
  }

  async function removeMember(uid) {
    if (!confirm(`¿Quitar a ${uid} de este bulk? Dejará de ver la oferta al instante.`)) return;
    setBusy(true);
    const res = await apiTry(`/api/backoffice/founders/reactivations/${bulkId}/members/${uid}`, {
      method: 'DELETE'
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.data?.error || `Error ${res.status}`);
      return;
    }
    await reload();
    onChanged?.();
  }

  async function resync() {
    setBusy(true);
    const res = await apiTry(`/api/backoffice/founders/reactivations/${bulkId}/resync`, {
      method: 'POST'
    });
    setBusy(false);
    setError(
      res.ok && res.data?.mirror?.failed === 0
        ? null
        : `Re-sincronización incompleta: ${res.data?.mirror?.failed ?? '?'} fallos.`
    );
  }

  const bulk = data?.bulk;
  const members = data?.members || [];

  // A qué fecha dejaría la ventana el selector de duración, para enseñarlo
  // antes de guardar. Null si no hay cambio pendiente o si es 'custom'.
  const durationPreview = useMemo(() => {
    if (!bulk || durationKey === 'custom' || durationKey === bulk.durationKey) return null;
    const ms = DURATION_MS[durationKey];
    if (ms === null || ms === undefined) return { endsAt: null, past: false };
    const endsAt = bulk.startsAt + ms;
    return { endsAt, past: endsAt <= Date.now() };
  }, [bulk, durationKey]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader className="flex items-center justify-between gap-2 pr-10">
            <span>{bulk?.name || 'Bulk'}</span>
            {bulk && bulkChip(bulk)}
          </ModalHeader>
          <ModalBody className="gap-4">
            {error && (
              <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>
            )}

            {loading && !data ? (
              <Spinner className="my-10 self-center" />
            ) : bulk ? (
              <>
                {/* Las tarjetas no encogen (shrink-0): el cuerpo del modal tiene el alto
                    limitado y, al encoger, cada una sacaba su propio scroll y en una
                    ventana baja se quedaba casi sin altura. */}
                <Card className="shrink-0">
                  <CardHeader className="pb-0 text-sm font-semibold">Ajustes del bulk</CardHeader>
                  <CardBody className="gap-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <Input
                        label="Nombre"
                        value={name}
                        onValueChange={setName}
                        isDisabled={!canWrite || busy}
                      />
                      <Select
                        label="Duración (aplica a quien hereda del bulk)"
                        selectedKeys={[durationKey]}
                        onSelectionChange={(keys) => setDurationKey([...keys][0])}
                        isDisabled={!canWrite || busy}
                      >
                        {[
                          ...DURATIONS,
                          // Solo visible si el plazo se amplió o se fijó a mano.
                          ...(durationKey === 'custom' ? [{ key: 'custom', label: DURATION_LABEL.custom }] : [])
                        ].map((d) => (
                          <SelectItem key={d.key}>{d.label}</SelectItem>
                        ))}
                      </Select>
                    </div>
                    {/* La duración se recalcula desde el inicio del bulk, así que
                        con un bulk viejo puede dar una fecha ya pasada. En vez de
                        dejar que se guarde a ciegas, se avisa y se manda a
                        "Ampliar el plazo", que cuenta desde ahora. */}
                    {durationPreview && (
                      <div
                        className={`rounded-md px-3 py-2 text-xs ${
                          durationPreview.past
                            ? 'bg-warning-50 text-warning-700'
                            : 'bg-default-100 text-default-600'
                        }`}
                      >
                        {durationPreview.past ? (
                          <>
                            Con esa duración el bulk terminaría el{' '}
                            <strong>{fmt(durationPreview.endsAt)}</strong>, que ya pasó —
                            se quedaría muerto. Usa «Ampliar el plazo» aquí abajo, que
                            cuenta desde ahora.
                          </>
                        ) : (
                          <>
                            Al guardar, el bulk terminará el{' '}
                            <strong>
                              {durationPreview.endsAt === null
                                ? 'nunca (para siempre)'
                                : fmt(durationPreview.endsAt)}
                            </strong>
                            .
                          </>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-default-500">
                      <span>Inicio: {fmt(bulk.startsAt)}</span>
                      <span>·</span>
                      <span>Fin: {bulk.forever ? 'para siempre' : fmt(bulk.endsAt)}</span>
                      <span>·</span>
                      <span>Creado: {fmt(bulk.createdAt)}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        color="primary"
                        isDisabled={!canWrite || busy}
                        onPress={() => patchBulk(
                          // 'custom' no es una duración que se pueda aplicar:
                          // si no la ha tocado, solo guardamos el nombre.
                          durationKey === 'custom'
                            ? { name: name.trim() }
                            : { name: name.trim(), durationKey }
                        )}
                      >
                        Guardar cambios
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        isDisabled={!canWrite || busy}
                        onPress={addMembers.onOpen}
                      >
                        Añadir usuarios
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        isDisabled={!canWrite || busy}
                        onPress={() => patchBulk({ status: bulk.status === 'active' ? 'archived' : 'active' })}
                      >
                        {bulk.status === 'active' ? 'Archivar bulk' : 'Reactivar bulk'}
                      </Button>
                      <Tooltip content="Reintenta escribir la ventana en la ficha de cada usuario (Firestore).">
                        <Button size="sm" variant="light" isDisabled={!canWrite || busy} onPress={resync}>
                          Re-sincronizar
                        </Button>
                      </Tooltip>
                    </div>
                  </CardBody>
                </Card>

                <Card className="shrink-0">
                  <CardHeader className="flex flex-col items-start gap-0 pb-0">
                    <span className="text-sm font-semibold">Ampliar el plazo</span>
                    <span className="text-xs font-normal text-default-400">
                      Suma tiempo sobre el fin actual sin tocar nada más. Si el bulk ya
                      había caducado, el plazo cuenta desde ahora.
                    </span>
                  </CardHeader>
                  <CardBody className="gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {EXTENSIONS.map((d) => (
                        <Button
                          key={d.key}
                          size="sm"
                          variant="flat"
                          color="primary"
                          isDisabled={!canWrite || busy || bulk.forever}
                          onPress={() => patchBulk({ extend: d.key })}
                        >
                          + {d.label}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        variant="flat"
                        isDisabled={!canWrite || busy || bulk.forever}
                        onPress={() => patchBulk({ endsAt: null })}
                      >
                        Quitar el límite (para siempre)
                      </Button>
                    </div>
                    {bulk.forever && (
                      <p className="text-xs text-default-400">
                        Este bulk no tiene fecha de fin, así que no hay plazo que ampliar.
                        Ponle una duración concreta arriba si quieres limitarlo.
                      </p>
                    )}
                    <div className="flex flex-wrap items-end gap-2">
                      <DatePicker
                        className="max-w-[280px]"
                        label="…o fija una fecha de fin exacta"
                        granularity="minute"
                        showMonthAndYearPickers
                        hideTimeZone
                        value={endValue}
                        onChange={setEndValue}
                        isDisabled={!canWrite || busy}
                      />
                      <Button
                        size="sm"
                        variant="flat"
                        isDisabled={!canWrite || busy || !endValue}
                        onPress={() => patchBulk({ endsAt: endValue.toDate().getTime() })}
                      >
                        Aplicar fecha
                      </Button>
                    </div>
                  </CardBody>
                </Card>

                <div className="flex shrink-0 flex-wrap gap-2">
                  <Chip size="sm" color="success" variant="flat">{bulk.counts.live} con oferta viva</Chip>
                  <Chip size="sm" variant="flat">{bulk.counts.active} activos</Chip>
                  <Chip size="sm" color="primary" variant="flat">{bulk.counts.converted} convertidos</Chip>
                  {bulk.counts.removed > 0 && (
                    <Chip size="sm" variant="flat">{bulk.counts.removed} retirados</Chip>
                  )}
                </div>

                {/* Un solo scroll, el del modal: la tabla va a su altura (sin overflow
                    propio, para que la cabecera fija se pegue arriba del modal al bajar). */}
                <div className="shrink-0 rounded-md border border-default-200">
                  <Table removeWrapper aria-label="Usuarios del bulk" isCompact isHeaderSticky>
                    <TableHeader>
                      <TableColumn>Usuario</TableColumn>
                      <TableColumn>Founder</TableColumn>
                      <TableColumn>Estado</TableColumn>
                      <TableColumn>Ventana</TableColumn>
                      <TableColumn>Duración</TableColumn>
                      <TableColumn align="end">Acciones</TableColumn>
                    </TableHeader>
                    <TableBody emptyContent="Sin usuarios">
                      {members.map((m) => (
                        <TableRow key={m.uid}>
                          <TableCell className="text-xs">
                            <div className="flex flex-col">
                              <span>{m.email || m.sourceRef || '—'}</span>
                              <span className="font-mono text-default-400">{m.uid}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            {m.founderNumber != null
                              ? `#${String(m.founderNumber).padStart(4, '0')}`
                              : <Chip size="sm" color="warning" variant="flat">Nunca lo fue</Chip>}
                          </TableCell>
                          <TableCell className="text-xs">
                            {m.status === 'converted' ? (
                              <Chip size="sm" color="primary" variant="flat">Convertido</Chip>
                            ) : m.status === 'removed' ? (
                              <Chip size="sm" variant="flat">Retirado</Chip>
                            ) : m.live ? (
                              <Chip size="sm" color="success" variant="flat">Viva</Chip>
                            ) : (
                              <Chip size="sm" variant="flat">Caducada</Chip>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {m.forever ? 'Para siempre' : `${fmt(m.endsAt)} · ${remainingLabel(m.endsAt)}`}
                          </TableCell>
                          <TableCell>
                            <Select
                              size="sm"
                              className="min-w-[150px]"
                              aria-label={`Duración de ${m.uid}`}
                              selectedKeys={[m.inherits ? 'inherit' : m.durationKey]}
                              isDisabled={!canWrite || busy || m.status !== 'active'}
                              onSelectionChange={(keys) => {
                                const key = [...keys][0];
                                patchMember(m.uid, { durationKey: key === 'inherit' ? null : key });
                              }}
                            >
                              <SelectItem key="inherit">
                                Hereda ({DURATION_LABEL[bulk.durationKey]})
                              </SelectItem>
                              {[
                                ...DURATIONS,
                                ...(m.durationKey === 'custom'
                                  ? [{ key: 'custom', label: DURATION_LABEL.custom }]
                                  : [])
                              ].map((d) => (
                                <SelectItem key={d.key}>{d.label}</SelectItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Tooltip content="Darle 24 h más solo a este usuario">
                                <Button
                                  size="sm"
                                  variant="flat"
                                  isDisabled={!canWrite || busy || m.status !== 'active' || m.forever}
                                  onPress={() => patchMember(m.uid, { extend: '24h' })}
                                >
                                  +24h
                                </Button>
                              </Tooltip>
                              <Button
                                size="sm"
                                variant="light"
                                color="danger"
                                isDisabled={!canWrite || busy}
                                onPress={() => removeMember(m.uid)}
                              >
                                Quitar
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {bulk && (
        <AddMembersModal
          isOpen={addMembers.isOpen}
          onClose={addMembers.onClose}
          bulk={bulk}
          onDone={() => { reload(); onChanged?.(); }}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Página
// ═══════════════════════════════════════════════════════════════════════════

export default function FoundersReactivations() {
  const { canWrite } = useAuth();
  const [bulks, setBulks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const create = useDisclosure();
  const detail = useDisclosure();

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/backoffice/founders/reactivations');
      setBulks(res.bulks || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function deleteBulk(bulk) {
    if (!confirm(`¿Borrar «${bulk.name}» y sus ${bulk.counts.active} usuarios? La oferta desaparecerá de sus apps.`)) return;
    try {
      await apiFetch(`/api/backoffice/founders/reactivations/${bulk.id}`, { method: 'DELETE' });
      reload();
    } catch (e) {
      setError(e.message);
    }
  }

  function openDetail(bulk) {
    setDetailId(bulk.id);
    detail.onOpen();
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-default-500">
          Cada bulk reabre la compra de Founders —al mismo precio de siempre— a un
          conjunto de usuarios durante la ventana que elijas, aunque la oferta global
          esté cerrada. La pestaña Founders reaparece en Gestionar Suscripción solo
          para ellos.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="flat" onPress={reload} isLoading={loading}>
            Refrescar
          </Button>
          <Button size="sm" color="primary" onPress={create.onOpen} isDisabled={!canWrite}>
            Nuevo bulk
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>
      )}

      {loading && !bulks.length ? (
        <Spinner className="mt-8 self-center" />
      ) : !bulks.length ? (
        <Card>
          <CardBody className="items-center gap-2 py-10 text-center text-sm text-default-500">
            <span>Todavía no hay ningún bulk de reactivación.</span>
            <Button size="sm" color="primary" onPress={create.onOpen} isDisabled={!canWrite}>
              Crear el primero
            </Button>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {bulks.map((b) => (
            <Card key={b.id}>
              <CardHeader className="flex items-start justify-between gap-2 pb-0">
                <div className="flex flex-col">
                  <span className="text-base font-semibold">{b.name}</span>
                  <span className="text-xs text-default-400">
                    {DURATION_LABEL[b.durationKey] || b.durationKey} · creado {fmt(b.createdAt)}
                  </span>
                </div>
                {bulkChip(b)}
              </CardHeader>
              <CardBody className="gap-3">
                <div className="flex flex-wrap gap-2">
                  <Chip size="sm" color="success" variant="flat">{b.counts.live} con oferta viva</Chip>
                  <Chip size="sm" variant="flat">{b.counts.active} activos</Chip>
                  <Chip size="sm" color="primary" variant="flat">{b.counts.converted} convertidos</Chip>
                </div>
                <div className="text-xs text-default-500">
                  Fin: {b.forever ? 'para siempre' : `${fmt(b.endsAt)} · ${remainingLabel(b.endsAt)}`}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" color="primary" variant="flat" onPress={() => openDetail(b)}>
                    Ver y editar
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    color="danger"
                    isDisabled={!canWrite}
                    onPress={() => deleteBulk(b)}
                  >
                    Borrar
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <CreateBulkModal
        isOpen={create.isOpen}
        onClose={create.onClose}
        onCreated={reload}
      />

      <BulkDetailModal
        bulkId={detailId}
        isOpen={detail.isOpen}
        onClose={detail.onClose}
        onChanged={reload}
        canWrite={canWrite}
      />
    </div>
  );
}
